import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { PageHeader } from '@/components/PageHeader';
import { LoadingSpinner } from '@/components/LoadingSpinner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const euro = (n) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(Number(n || 0));
const MESI = ['', 'gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];

// Cruscotto del condomino: solo la SUA posizione. Niente totali del condominio e niente criticità.
export const ResidentDashboard = () => {
  const { token, selectedCondominium } = useAuth();
  const condoId = selectedCondominium?.condominium?.id;
  const [d, setD] = useState(null);
  const [errore, setErrore] = useState('');
  const [dichiarazioni, setDichiarazioni] = useState([]);
  const [avvisi, setAvvisi] = useState([]);
  const [sel, setSel] = useState({});
  const [metodo, setMetodo] = useState('BONIFICO');
  const [dataPag, setDataPag] = useState('');
  const [note, setNote] = useState('');
  const [files, setFiles] = useState([]);
  const [invio, setInvio] = useState(false);
  const intest = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const base = `${API_URL}/api/condominiums/${condoId}`;

  const carica = useCallback(async () => {
    if (!condoId) return;
    try {
      const res = await fetch(`${base}/my-dashboard`, { headers: intest });
      if (!res.ok) throw new Error('Non riesco a caricare la tua posizione');
      setD(await res.json());
      const r2 = await fetch(`${base}/dichiarazioni-pagamento`, { headers: intest });
      if (r2.ok) setDichiarazioni((await r2.json()).dichiarazioni || []);
      // ultime comunicazioni dell'amministrazione (le stesse delle «Comunicazioni Attive»)
      const r3 = await fetch(`${base}/communications`, { headers: intest });
      if (r3.ok) {
        const tutte = await r3.json();
        setAvvisi((Array.isArray(tutte) ? tutte : [])
          .sort((a, b) => String(b.published_at || b.created_at || '').localeCompare(String(a.published_at || a.created_at || '')))
          .slice(0, 3));
      }
    } catch (e) { setErrore(e.message); }
  }, [base, condoId, intest]);

  useEffect(() => { carica(); }, [carica]);

  const aperte = d?.quote_aperte || [];
  const scelte = aperte.filter((q) => sel[q.id]);
  const totaleScelto = scelte.reduce((t, q) => t + Number(q.residuo || 0), 0);
  const oggi = new Date().toISOString().slice(0, 10);

  const dichiara = async () => {
    setInvio(true);
    try {
      const fd = new FormData();
      fd.append('quote_ids', JSON.stringify(scelte.map((q) => q.id)));
      fd.append('metodo', metodo);
      if (dataPag) fd.append('data_bonifico', dataPag);
      if (note.trim()) fd.append('note', note.trim());
      files.forEach((f) => fd.append('ricevute', f));
      const res = await fetch(`${base}/dichiarazioni-pagamento`, { method: 'POST', headers: intest, body: fd });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof j.detail === 'string' ? j.detail : 'Dichiarazione non riuscita');
      toast.success(j.info || 'Dichiarazione ricevuta');
      setSel({}); setFiles([]); setNote(''); setDataPag('');
      await carica();
    } catch (e) { toast.error(e.message); }
    setInvio(false);
  };

  if (errore) return <div className="p-6 text-sm text-red-700">{errore}</div>;
  if (!d) return <div className="p-10 flex justify-center"><LoadingSpinner size="lg" text="Caricamento..." /></div>;

  const max = Math.max(1, ...d.versato_per_anno.map((x) => x.importo));
  const t = d.totali;

  return (
    <div className="space-y-6">
      <PageHeader
        title={d.persona ? `Ciao, ${d.persona}` : 'La tua posizione'}
        description={d.unita.length ? `Unità: ${d.unita.join(', ')}` : selectedCondominium?.condominium?.name}
      />

      {avvisi.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Avvisi dell'amministrazione</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {avvisi.map((a) => {
              const quando = String(a.published_at || a.created_at || '').slice(0, 10);
              const nuovo = quando && (Date.now() - new Date(quando).getTime()) < 7 * 86400000;
              return (
                <div key={a.id} className="border-b pb-3 last:border-0 last:pb-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-sm">{a.title}</span>
                    {nuovo && <Badge className="bg-emerald-100 text-emerald-800">Nuovo</Badge>}
                    <span className="text-xs text-muted-foreground">{quando.split('-').reverse().join('/')}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1 whitespace-pre-line">{(a.content || '').length > 260 ? `${a.content.slice(0, 260)}…` : a.content}</p>
                </div>
              );
            })}
            <Link className="text-sm underline" to="/communications">Vedi tutte le comunicazioni</Link>
          </CardContent>
        </Card>
      )}

      {!d.collegato && (
        <Card><CardContent className="pt-6 text-sm">
          Il tuo accesso non è ancora collegato a un'unità. Contatta l'amministrazione.
        </CardContent></Card>
      )}

      <div className="grid gap-4 grid-cols-1 sm:grid-cols-3">
        <Card><CardHeader className="pb-2"><CardDescription>Versato</CardDescription><CardTitle className="text-2xl">{euro(t.pagato)}</CardTitle></CardHeader>
          <CardContent className="text-xs text-muted-foreground">{t.mensilita_pagate} mensilità saldate</CardContent></Card>
        <Card><CardHeader className="pb-2"><CardDescription>Da versare</CardDescription><CardTitle className="text-2xl">{euro(t.da_pagare)}</CardTitle></CardHeader>
          <CardContent className="text-xs text-muted-foreground">{t.mensilita_aperte} mensilità aperte</CardContent></Card>
        <Card><CardHeader className="pb-2"><CardDescription>Di cui già scaduto</CardDescription><CardTitle className="text-2xl">{euro(t.da_pagare_scaduto)}</CardTitle></CardHeader>
          <CardContent className="text-xs text-muted-foreground">fino al mese in corso</CardContent></Card>
      </div>

      {d.versato_per_anno.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Quanto hai versato, anno per anno</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {d.versato_per_anno.map((x) => (
              <div key={x.anno} className="flex items-center gap-3 text-sm">
                <span className="w-12 text-muted-foreground">{x.anno}</span>
                <div className="flex-1 bg-muted rounded h-4"><div className="bg-primary h-4 rounded" style={{ width: `${Math.max(2, (x.importo / max) * 100)}%` }} /></div>
                <span className="w-28 text-right tabular-nums">{euro(x.importo)}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">Situazione per anno</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow><TableHead>Anno</TableHead><TableHead className="text-right">Dovuto</TableHead><TableHead className="text-right">Pagato</TableHead><TableHead className="text-right">Da pagare</TableHead></TableRow></TableHeader>
            <TableBody>
              {d.per_anno.map((x) => (
                <TableRow key={x.anno}>
                  <TableCell>{x.anno}</TableCell>
                  <TableCell className="text-right tabular-nums">{euro(x.dovuto)}</TableCell>
                  <TableCell className="text-right tabular-nums">{euro(x.pagato)}</TableCell>
                  <TableCell className="text-right tabular-nums">{x.da_pagare > 0.004 ? <strong>{euro(x.da_pagare)}</strong> : <Badge className="bg-green-100 text-green-800">saldato</Badge>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {aperte.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Quote da pagare</CardTitle>
            <CardDescription>Spunta le quote che intendi pagare: il totale si calcola da solo. Sotto puoi allegare la ricevuta e dichiarare il pagamento.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Table>
              <TableHeader><TableRow><TableHead className="w-10"></TableHead><TableHead>Mese</TableHead><TableHead>Unità</TableHead><TableHead className="text-right">Dovuto</TableHead><TableHead className="text-right">Pagato</TableHead><TableHead className="text-right">Residuo</TableHead></TableRow></TableHeader>
              <TableBody>
                {aperte.map((q) => (
                  <TableRow key={q.id}>
                    <TableCell>
                      <input type="checkbox" aria-label={`Seleziona ${MESI[q.mese]} ${q.anno}`} disabled={q.in_verifica}
                        checked={Boolean(sel[q.id])} onChange={(e) => setSel({ ...sel, [q.id]: e.target.checked })} />
                    </TableCell>
                    <TableCell>
                      {MESI[q.mese]} {q.anno}
                      {q.scaduta ? <Badge className="ml-1 bg-red-100 text-red-800">scaduta</Badge> : null}
                      {q.in_verifica ? <Badge className="ml-1 bg-yellow-100 text-yellow-800">pagamento in verifica</Badge> : null}
                    </TableCell>
                    <TableCell>{q.unita}</TableCell>
                    <TableCell className="text-right tabular-nums">{euro(q.dovuto)}</TableCell>
                    <TableCell className="text-right tabular-nums">{euro(q.pagato)}</TableCell>
                    <TableCell className="text-right tabular-nums"><strong>{euro(q.residuo)}</strong></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-muted p-3">
              <div className="flex gap-2">
                <Button type="button" size="sm" variant="outline" onClick={() => setSel(Object.fromEntries(aperte.filter((q) => q.scaduta && !q.in_verifica).map((q) => [q.id, true])))}>Seleziona le scadute</Button>
                <Button type="button" size="sm" variant="outline" onClick={() => setSel({})}>Deseleziona</Button>
              </div>
              <div className="text-sm">{scelte.length} quote selezionate · totale <strong className="text-lg tabular-nums">{euro(totaleScelto)}</strong></div>
            </div>

            {scelte.length > 0 && (
              <div className="space-y-3 rounded-md border p-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label>Come hai pagato</Label>
                    <select className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm" value={metodo} onChange={(e) => setMetodo(e.target.value)}>
                      <option value="BONIFICO">Bonifico</option>
                      <option value="BANCOMAT">Bancomat</option>
                      <option value="POS">Carta / POS</option>
                      <option value="ASSEGNO">Assegno</option>
                      <option value="CONTANTI">Contanti</option>
                      <option value="ALTRO">Altro metodo</option>
                    </select>
                  </div>
                  <div>
                    <Label>Data del pagamento</Label>
                    <Input type="date" max={oggi} value={dataPag} onChange={(e) => setDataPag(e.target.value)} />
                  </div>
                </div>
                <div>
                  <Label>Ricevuta del bonifico o di altro metodo di pagamento (PDF, JPG, PNG, WebP: fino a 3 file da 8 MB)</Label>
                  <Input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 3))} />
                  {files.length > 0 && <p className="text-xs text-muted-foreground mt-1">{files.map((f) => f.name).join(', ')}</p>}
                </div>
                <div>
                  <Label>Note (facoltative)</Label>
                  <Input value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} placeholder="Per esempio il numero di CRO o un riferimento" />
                </div>
                <Button onClick={dichiara} disabled={invio || files.length === 0}>Pagato salvo verifica buon fine pagamento</Button>
                <p className="text-xs text-muted-foreground">
                  Le quote restano da pagare finché l'amministrazione non verifica l'accredito. Appena verificato, si aggiornano da sole.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {dichiarazioni.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">I tuoi pagamenti dichiarati</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            {dichiarazioni.map((x) => (
              <div key={x.id} className="flex flex-wrap items-center justify-between gap-2 border-b pb-2 last:border-0">
                <div>
                  {euro(x.totale)} su {x.voci.length} quote · {(x.creato_il || '').slice(0, 10).split('-').reverse().join('/')}
                  {x.stato === 'RIFIUTATA' && x.motivo_rifiuto ? <div className="text-red-700">Motivo: {x.motivo_rifiuto}</div> : null}
                </div>
                <Badge className={x.stato === 'VERIFICATA' ? 'bg-green-100 text-green-800' : x.stato === 'RIFIUTATA' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}>
                  {x.stato === 'VERIFICATA' ? 'Verificato' : x.stato === 'RIFIUTATA' ? 'Non verificato' : 'In verifica'}
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {d.ultimi_pagamenti.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Ultimi pagamenti registrati</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Periodo</TableHead><TableHead>Unità</TableHead><TableHead className="text-right">Importo</TableHead></TableRow></TableHeader>
              <TableBody>
                {d.ultimi_pagamenti.map((p, i) => (
                  <TableRow key={i}><TableCell>{p.data.split('-').reverse().join('/')}</TableCell><TableCell>{p.periodo}</TableCell><TableCell>{p.unita}</TableCell><TableCell className="text-right tabular-nums">{euro(p.importo)}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <p className="text-xs text-muted-foreground">
        Il dettaglio mese per mese e le ricevute sono in <Link className="underline" to="/monthly-quotes">Le mie quote</Link>.
        Gli avvisi dell'amministrazione sono in <Link className="underline" to="/my-communications">Le mie comunicazioni</Link>.
      </p>
    </div>
  );
};

export default ResidentDashboard;
